/**
 * Enregistreur de message vocal — UX style WhatsApp.
 *
 * Mode toggle (pas de hold) :
 * - Tap sur micro → l'interface bascule en mode enregistrement
 * - Pendant l'enregistrement, 3 boutons visibles :
 *   • Corbeille (annuler)
 *   • Chronomètre + waveform en temps réel
 *   • Bouton vert "envoyer" (stop + envoi immédiat)
 * - Si l'utilisateur ferme la page ou navigue : auto-cancel
 *
 * Codec Opus dans WebM (ou MP4 selon support) — ~6-12 KB / 5 sec.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, Trash2, Send, Loader2 } from 'lucide-react';

interface Props {
  onRecorded: (blob: Blob, dureeSec: number) => Promise<void> | void;
  onPhaseChange?: (phase: 'idle' | 'recording' | 'sending') => void;
  disabled?: boolean;
  maxDurationSec?: number;
}

function pickMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];
  for (const mt of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mt)) return mt;
  }
  return '';
}

export const AudioRecorder: React.FC<Props> = ({
  onRecorded, onPhaseChange, disabled, maxDurationSec = 120,
}) => {
  const [phase, setPhase] = useState<'idle' | 'recording' | 'sending'>('idle');
  useEffect(() => { onPhaseChange?.(phase); }, [phase, onPhaseChange]);
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const cancelledRef = useRef<boolean>(false);

  const cleanupAudio = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      try { audioCtxRef.current.close(); } catch { /* */ }
    }
    audioCtxRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // Au démontage : annuler tout
  useEffect(() => () => {
    cancelledRef.current = true;
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      try { mediaRecRef.current.stop(); } catch { /* */ }
    }
    cleanupAudio();
  }, []);

  const startRecording = async () => {
    if (disabled || phase !== 'idle') return;
    setError(null);
    cancelledRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Analyser pour visualiser le niveau audio
      try {
        const AC = window.AudioContext || (window as any).webkitAudioContext;
        const ctx: AudioContext = new AC();
        audioCtxRef.current = ctx;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        const src = ctx.createMediaStreamSource(stream);
        src.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) sum += data[i];
          setLevel(Math.min(1, sum / data.length / 80));
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch { /* analyser optionnel */ }

      const mt = pickMimeType();
      const rec = new MediaRecorder(stream, mt ? { mimeType: mt } : undefined);
      mediaRecRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const dureeFinale = Math.max(1, Math.round((Date.now() - startTimeRef.current) / 1000));
        const blob = new Blob(chunksRef.current, { type: mt || 'audio/webm' });
        cleanupAudio();
        if (cancelledRef.current) {
          setPhase('idle');
          setSeconds(0);
          return;
        }
        // Envoi direct
        setPhase('sending');
        try {
          await onRecorded(blob, dureeFinale);
          setPhase('idle');
          setSeconds(0);
        } catch (e: any) {
          setError(e?.message || 'Erreur d\'envoi');
          setPhase('idle');
          setSeconds(0);
        }
      };
      rec.start();
      startTimeRef.current = Date.now();
      setPhase('recording');
      setSeconds(0);
      tickRef.current = window.setInterval(() => {
        const sec = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setSeconds(sec);
        if (sec >= maxDurationSec) sendRecording();
      }, 200);
    } catch (e: any) {
      setError(e?.name === 'NotAllowedError'
        ? 'Microphone refusé. Autorisez l\'accès dans les réglages.'
        : 'Microphone indisponible.');
      cleanupAudio();
      setPhase('idle');
    }
  };

  const cancelRecording = () => {
    if (phase !== 'recording') return;
    cancelledRef.current = true;
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      try { mediaRecRef.current.stop(); } catch { /* */ }
    }
  };

  const sendRecording = () => {
    if (phase !== 'recording') return;
    cancelledRef.current = false;
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      try { mediaRecRef.current.stop(); } catch { /* */ }
    }
  };

  // ─── Phase ENVOI EN COURS ────────────────────────────────────
  if (phase === 'sending') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-900/40 border border-blue-700 rounded-full">
        <Loader2 size={16} className="text-blue-300 animate-spin" />
        <span className="text-xs text-blue-200">Envoi…</span>
      </div>
    );
  }

  // ─── Phase ENREGISTREMENT ────────────────────────────────────
  if (phase === 'recording') {
    return (
      <div className="flex items-center gap-2">
        {/* Annuler */}
        <button
          onClick={cancelRecording}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-800 hover:bg-red-900/40 text-gray-300 hover:text-red-400 transition-colors"
          title="Annuler"
        >
          <Trash2 size={16} />
        </button>

        {/* Chrono + waveform */}
        <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-red-900/30 border border-red-700/50 rounded-full min-w-[140px]">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
          <span className="font-mono text-xs text-red-200 tabular-nums flex-shrink-0">
            {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
          </span>
          <div className="flex-1 flex items-end gap-0.5 h-5 overflow-hidden">
            {Array.from({ length: 14 }).map((_, i) => {
              const threshold = (i + 1) / 14;
              const active = level >= threshold * 0.5;
              const h = active ? 6 + Math.round(level * 14) : 3;
              return (
                <span
                  key={i}
                  className={`w-0.5 rounded-sm transition-all ${active ? 'bg-red-300' : 'bg-red-800/50'}`}
                  style={{ height: `${h}px` }}
                />
              );
            })}
          </div>
        </div>

        {/* Envoyer (vert WhatsApp) */}
        <button
          onClick={sendRecording}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-green-600 hover:bg-green-700 text-white transition-colors shadow-lg"
          title="Envoyer"
        >
          <Send size={16} />
        </button>
      </div>
    );
  }

  // ─── Phase IDLE ───────────────────────────────────────────────
  return (
    <div className="flex items-center gap-2">
      {error && (
        <span className="text-[10px] text-red-400 max-w-[150px] truncate" title={error}>
          {error}
        </span>
      )}
      <button
        type="button"
        onClick={startRecording}
        disabled={disabled}
        className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white transition-colors"
        title="Enregistrer un message vocal"
      >
        <Mic size={16} />
      </button>
    </div>
  );
};
